import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'

export interface SubmitConfirmDialogProps {
  open: boolean
  submitting: boolean
  onConfirm: () => void
  onCancel: () => void
}

/** Confirms a submission; the assessment can't be edited afterward. */
export function SubmitConfirmDialog({
  open,
  submitting,
  onConfirm,
  onCancel,
}: SubmitConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  // Read through refs so the focus/keydown effect runs once per open, not per re-render.
  const latest = useRef({ submitting, onCancel })
  latest.current = { submitting, onCancel }

  useEffect(() => {
    if (!open) return
    // Restores focus to whatever opened the dialog (the Submit button) on close.
    const opener = document.activeElement as HTMLElement | null
    cancelRef.current?.focus()
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !latest.current.submitting) latest.current.onCancel()
      if (e.key !== 'Tab' || !dialogRef.current) return
      // aria-modal does not stop Tab reaching the page behind the overlay, so cycle
      // within the dialog's enabled buttons, or hold the container while there are none.
      const buttons = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'),
      )
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      const active = document.activeElement
      if (!first || !last) {
        e.preventDefault()
        dialogRef.current.focus()
      } else if (e.shiftKey && (active === first || active === dialogRef.current)) {
        // The container itself can hold focus (clicked body, or buttons just re-enabled).
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      opener?.focus()
    }
  }, [open])

  // Disabling the focused button would drop focus to the page; keep it in the dialog.
  useEffect(() => {
    if (open && submitting) dialogRef.current?.focus()
  }, [open, submitting])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="submit-confirm-title"
        aria-describedby="submit-confirm-body"
        className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-card"
      >
        <h2 id="submit-confirm-title" className="text-lg font-semibold text-heading">
          Submit this assessment?
        </h2>
        <p id="submit-confirm-body" className="mt-2 text-[15px] text-body">
          Once submitted, the assessment can’t be edited. Make sure every answer is final.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button ref={cancelRef} variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={submitting} aria-busy={submitting}>
            {submitting ? 'Submitting…' : 'Submit assessment'}
          </Button>
        </div>
      </div>
    </div>
  )
}
